// gameDetailsCache.js - Shared, process-wide cache for game details.
//
// Bubble/activity charts previously fetched /games/:id/details on every render
// (and once per chart instance), causing duplicate backend calls when switching
// tabs, entering fullscreen or re-sharing. This module deduplicates those calls
// with a simple promise cache that persists for the lifetime of the page.

import { API_BASE } from "../config/api";
import { httpRequest } from "./httpClient";
import { authService } from "../services/auth";
import logger from "./logger";

const cache = new Map();
const inFlight = new Map();

/**
 * Fetch game details for the given id, returning a cached result when available.
 * Concurrent callers for the same id share a single in-flight request.
 *
 * @param {string|number} gameId
 * @returns {Promise<object|null>} game details, or null if the request fails.
 */
export async function getGameDetails(gameId) {
  if (!gameId) return null;
  const key = String(gameId);

  if (cache.has(key)) {
    return cache.get(key);
  }

  if (inFlight.has(key)) {
    return inFlight.get(key);
  }

  const promise = (async () => {
    try {
      const response = await httpRequest(`${API_BASE}/games/${gameId}/details`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        return null;
      }

      const details = await response.json();
      cache.set(key, details);
      return details;
    } catch (error) {
      logger.warn("Failed to fetch game details for", gameId, error);
      return null;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
}

/**
 * Return cached details without triggering a fetch.
 *
 * @param {string|number} gameId
 * @returns {object|null}
 */
export function peekGameDetails(gameId) {
  return cache.get(String(gameId)) || null;
}

/**
 * Clear the cache. Useful in tests to guarantee isolation between cases.
 */
export function clearGameDetailsCache() {
  cache.clear();
  inFlight.clear();
}
